import React, { useState } from "react";
import {
	Box,
	Typography,
	IconButton,
	TextField,
	Button,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { BasicModal } from "../BasicModal/BasicModal";
import { useWindowSize } from "@uidotdev/usehooks";
import {
	TRAVEL_KIT_MODAL,
	textFieldSx,
	modalContentSx,
	primaryButtonSx,
	secondaryButtonSx,
} from "./TravelKitModalStyles";

// Template104: Travel Kit - Presupuesto del Viaje
const Template104 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	registerScrollContainer,
	isViewer = false,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const setScrollContainerRef = (domNode) => {
		if (typeof registerScrollContainer === "function") {
			registerScrollContainer(domNode);
		}
	};

	const pageData = page.data || {};
	const [budgetData, setBudgetData] = useState(() => ({
		currency: pageData.currency || "€",
		totalBudget: pageData.totalBudget || 0,
		categories: pageData.categories || [
			{
				id: 1,
				name: "Transporte",
				emoji: "✈️",
				color: "#3B82F6",
				expenses: [{ id: 1, description: "Vuelos", amount: 0 }],
			},
			{
				id: 2,
				name: "Alojamiento",
				emoji: "🏨",
				color: "#10B981",
				expenses: [{ id: 1, description: "Hotel", amount: 0 }],
			},
			{
				id: 3,
				name: "Comida",
				emoji: "🍜",
				color: "#F59E0B",
				expenses: [],
			},
			{
				id: 4,
				name: "Actividades",
				emoji: "🎢",
				color: "#8B5CF6",
				expenses: [],
			},
			{
				id: 5,
				name: "Compras",
				emoji: "🛍️",
				color: "#EC4899",
				expenses: [],
			},
			{
				id: 6,
				name: "Otros",
				emoji: "📦",
				color: "#64748B",
				expenses: [],
			},
		],
	}));

	const [editModal, setEditModal] = useState(false);
	const [addExpenseModal, setAddExpenseModal] = useState(false);
	const [editExpenseModal, setEditExpenseModal] = useState(false);
	const [editBudgetModal, setEditBudgetModal] = useState(false);
	const [selectedCategory, setSelectedCategory] = useState(null);
	// Si no null, estamos editando este gasto: { categoryIndex, expenseIndex }
	const [editingExpense, setEditingExpense] = useState(null);
	const [tempExpense, setTempExpense] = useState({
		description: "",
		amount: "",
	});
	const [tempBudget, setTempBudget] = useState(budgetData.totalBudget);

	const updatePageData = (newData) => {
		updatePage({
			...page,
			data: { ...pageData, ...newData },
		});
	};

	const getTotalSpent = () => {
		return budgetData.categories.reduce((total, cat) => {
			return (
				total +
				cat.expenses.reduce(
					(sum, exp) => sum + (parseFloat(exp.amount) || 0),
					0,
				)
			);
		}, 0);
	};

	const getCategoryTotal = (category) => {
		return category.expenses.reduce(
			(sum, exp) => sum + (parseFloat(exp.amount) || 0),
			0,
		);
	};

	const getRemaining = () => {
		return budgetData.totalBudget - getTotalSpent();
	};

	const handleAddExpense = (categoryIndex) => {
		setSelectedCategory(categoryIndex);
		setEditingExpense(null);
		setTempExpense({ description: "", amount: "" });
		setAddExpenseModal(true);
		setEditExpenseModal(false);
	};

	const handleOpenEditExpense = (categoryIndex, expenseIndex) => {
		const expense = budgetData.categories[categoryIndex].expenses[expenseIndex];
		setSelectedCategory(categoryIndex);
		setEditingExpense({ categoryIndex, expenseIndex });
		setTempExpense({
			description: expense.description || "",
			amount: expense.amount !== undefined && expense.amount !== null ? String(expense.amount) : "",
		});
		setAddExpenseModal(false);
		setEditExpenseModal(true);
	};

	const handleCloseExpenseModal = () => {
		setAddExpenseModal(false);
		setEditExpenseModal(false);
		setEditingExpense(null);
	};

	const handleSaveExpense = () => {
		if (!tempExpense.description || !tempExpense.amount) return;

		const newCategories = [...budgetData.categories];

		if (editingExpense) {
			// Editar gasto existente
			const { categoryIndex, expenseIndex } = editingExpense;
			const expense = newCategories[categoryIndex].expenses[expenseIndex];
			newCategories[categoryIndex].expenses[expenseIndex] = {
				...expense,
				description: tempExpense.description,
				amount: parseFloat(tempExpense.amount) || 0,
			};
		} else {
			// Añadir nuevo gasto
			const newId =
				Math.max(
					0,
					...newCategories[selectedCategory].expenses.map((e) => e.id),
					0,
				) + 1;
			newCategories[selectedCategory].expenses.push({
				id: newId,
				description: tempExpense.description,
				amount: parseFloat(tempExpense.amount) || 0,
			});
		}

		const newData = { ...budgetData, categories: newCategories };
		setBudgetData(newData);
		updatePageData(newData);
		handleCloseExpenseModal();
	};

	const handleDeleteExpense = (categoryIndex, expenseIndex) => {
		const newCategories = [...budgetData.categories];
		newCategories[categoryIndex].expenses.splice(expenseIndex, 1);
		const newData = { ...budgetData, categories: newCategories };
		setBudgetData(newData);
		updatePageData(newData);
	};

	const handleSaveBudget = () => {
		const newData = { ...budgetData, totalBudget: parseFloat(tempBudget) || 0 };
		setBudgetData(newData);
		updatePageData(newData);
		setEditBudgetModal(false);
	};

	const handleChangeCurrency = (currency) => {
		const newData = { ...budgetData, currency };
		setBudgetData(newData);
		updatePageData(newData);
	};

	const formatMoney = (amount) => {
		return `${budgetData.currency}${amount.toFixed(2)}`;
	};

	const totalSpent = getTotalSpent();
	const remaining = getRemaining();
	const percentSpent =
		budgetData.totalBudget > 0
			? Math.min(100, Math.round((totalSpent / budgetData.totalBudget) * 100))
			: 0;

	const templateItemProps = {
		"data-template-item": true,
		className: "template-item",
	};

	return (
		<Box
			{...templateItemProps}
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				width: "100%",
				backgroundColor: backgroundColor || "#FFF8F5",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				position: "relative",
				zIndex: 1,
				pointerEvents: "none",
				"& [data-template-item]": { pointerEvents: "auto" },
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			{/* Header: washi strip + sticker Presupuesto */}
			<Box
				data-block-id="header"
				sx={{
					position: "sticky",
					top: 0,
					zIndex: 10,
					padding: isSmallScreen ? "12px 16px" : "16px 20px",
					backgroundColor: "rgb(248, 250, 252)",
					backgroundImage: `repeating-linear-gradient(
						95deg,
						transparent,
						transparent 6px,
						rgba(245, 230, 200, 0.5) 6px,
						rgba(245, 230, 200, 0.5) 9px
					)`,
					borderBottom: "3px dashed rgba(92, 58, 69, 0.2)",
					boxShadow: "0 4px 12px rgba(245, 230, 200, 0.4)",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						marginBottom: 1.5,
						flexWrap: "wrap",
						gap: 1,
					}}
				>
					<Box
						sx={{
							display: "inline-flex",
							alignItems: "center",
							gap: 1.5,
							backgroundColor: "rgba(255,255,255,0.95)",
							borderRadius: "24px",
							padding: "10px 18px",
							boxShadow:
								"0 3px 14px rgba(232, 180, 188, 0.3), 0 1px 3px rgba(0,0,0,0.06)",
							border: "2px solid rgba(245, 230, 200, 0.9)",
							transform: "rotate(-1.2deg)",
						}}
					>
						<AccountBalanceWalletIcon sx={{ fontSize: 22, color: "#B8956E" }} />
						<Typography
							sx={{
								fontSize: "17px !important",
								fontWeight: "800 !important",
								color: "#5C3A45",
							}}
						>
							✿ Presupuesto ✿
						</Typography>
					</Box>
					<Box {...templateItemProps} sx={{ pointerEvents: "auto", minWidth: 70 }}>
						<Select
							value={budgetData.currency}
							onChange={(e) => handleChangeCurrency(e.target.value)}
							size="small"
							sx={{
								color: "#5C3A45",
								backgroundColor: "rgba(255,255,255,0.9)",
								borderRadius: "14px",
								border: "2px dashed rgba(92, 58, 69, 0.2)",
								"& .MuiSelect-icon": { color: "#5C3A45" },
								"& .MuiOutlinedInput-notchedOutline": { border: "none" },
								fontSize: "14px",
								width: "100%",
							}}
						>
						<MenuItem value="€">EUR €</MenuItem>
						<MenuItem value="$">USD $ (Dólar estadounidense)</MenuItem>
						<MenuItem value="CAD$">CAD $ (Dólar canadiense)</MenuItem>
						<MenuItem value="MXN$">MXN $ (Peso mexicano)</MenuItem>
						<MenuItem value="GTQ">GTQ Q (Quetzal guatemalteco)</MenuItem>
						<MenuItem value="HNL">HNL L (Lempira hondureño)</MenuItem>
						<MenuItem value="NIO">NIO C$ (Córdoba nicaragüense)</MenuItem>
						<MenuItem value="CRC">CRC ₡ (Colón costarricense)</MenuItem>
						<MenuItem value="PAB">PAB B/. (Balboa panameño)</MenuItem>
						<MenuItem value="BZD">BZD $ (Dólar beliceño)</MenuItem>
						<MenuItem value="JMD">JMD $ (Dólar jamaicano)</MenuItem>
						<MenuItem value="DOP">DOP RD$ (Peso dominicano)</MenuItem>
						<MenuItem value="CUP">CUP $ (Peso cubano)</MenuItem>
						<MenuItem value="ARS$">ARS $ (Peso argentino)</MenuItem>
						<MenuItem value="R$">BRL R$ (Real brasileño)</MenuItem>
						<MenuItem value="CLP$">CLP $ (Peso chileno)</MenuItem>
						<MenuItem value="COL$">COP $ (Peso colombiano)</MenuItem>
						<MenuItem value="S/">S/ (Sol peruano)</MenuItem>
						<MenuItem value="₲">PYG ₲ (Guaraní paraguayo)</MenuItem>
						<MenuItem value="Bs.">BOB Bs. (Boliviano)</MenuItem>
						<MenuItem value="U$U">UYU $U (Peso uruguayo)</MenuItem>
						<MenuItem value="VEF">Bs. (Bolívar venezolano)</MenuItem>
						<MenuItem value="TT$">TTD $ (Dólar trinitense)</MenuItem>
						<MenuItem value="GYD$">GYD $ (Dólar guyanés)</MenuItem>
						<MenuItem value="SRD$">SRD $ (Dólar surinamés)</MenuItem>
						<MenuItem value="BSD$">BSD $ (Dólar bahameño)</MenuItem>
						<MenuItem value="BBD$">BBD $ (Dólar barbadense)</MenuItem>
						<MenuItem value="XCD">XCD $ (Dólar del Caribe Oriental)</MenuItem>
						<MenuItem value="£">GBP £ (Libra esterlina)</MenuItem>
						<MenuItem value="¥">JPY ¥ (Yen japonés)</MenuItem>
					</Select>
					</Box>
				</Box>

				{/* Budget Summary - recorte con cinta */}
				<Box sx={{ position: "relative", transform: "rotate(0.8deg)" }}>
					<Box
						sx={{
							position: "absolute",
							top: 0,
							left: 0,
							width: 24,
							height: 24,
							backgroundColor: "rgba(245, 230, 200, 0.9)",
							borderRadius: "0 0 8px 0",
							borderRight: "2px dashed rgba(92, 58, 69, 0.15)",
							borderBottom: "2px dashed rgba(92, 58, 69, 0.15)",
							zIndex: 1,
						}}
					/>
					<Box
						{...templateItemProps}
						onClick={() => {
							if (isViewer) return;
							setTempBudget(budgetData.totalBudget);
							setEditBudgetModal(true);
						}}
						sx={{
							backgroundColor: "rgba(255,255,255,0.95)",
							borderRadius: "16px",
							padding: "16px",
							cursor: isViewer ? "default" : "pointer",
							border: "2px dashed rgba(92, 58, 69, 0.25)",
							boxShadow:
								"0 6px 24px rgba(232, 180, 188, 0.18), 0 2px 6px rgba(0,0,0,0.06)",
							"&:hover": {
								backgroundColor: "#fff",
								boxShadow: "0 8px 28px rgba(232, 180, 188, 0.22)",
							},
						}}
					>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "flex-start",
								mb: 1,
							}}
						>
							<Box>
								<Typography
									sx={{
										fontSize: "11px !important",
										color: "#5C3A45",
										opacity: 0.8,
									}}
								>
									PRESUPUESTO TOTAL
								</Typography>
								<Typography
									sx={{
										fontSize: "24px !important",
										fontWeight: "800 !important",
										color: "#5C3A45",
									}}
								>
									{formatMoney(budgetData.totalBudget)}
								</Typography>
							</Box>
							<EditIcon className="hide-in-viewer" sx={{ fontSize: 18, color: "#5C3A45", opacity: 0.6, pointerEvents: "none" }} />
						</Box>

						{/* Progress bar */}
						<Box sx={{ mt: 2 }}>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									mb: 0.5,
								}}
							>
								<Typography
									sx={{
										fontSize: "11px !important",
										color: "#5C3A45",
										opacity: 0.8,
									}}
								>
									Gastado: {formatMoney(totalSpent)}
								</Typography>
								<Typography
									sx={{
										fontSize: "11px !important",
										color: "#5C3A45",
										opacity: 0.8,
									}}
								>
									{percentSpent}%
								</Typography>
							</Box>
							<Box
								sx={{
									height: 6,
									backgroundColor: "rgba(92, 58, 69, 0.15)",
									borderRadius: 3,
									overflow: "hidden",
								}}
							>
								<Box
									sx={{
										height: "100%",
										width: `${percentSpent}%`,
										backgroundColor: remaining >= 0 ? "#C5E8D4" : "#F5C6D8",
										borderRadius: 3,
										transition: "width 0.3s ease",
									}}
								/>
							</Box>
							<Typography
								sx={{
									fontSize: "12px !important",
									fontWeight: "600 !important",
									mt: 1,
									color: remaining >= 0 ? "#2D5A3D" : "#8B3A4A",
								}}
							>
								{remaining >= 0
									? `Disponible: ${formatMoney(remaining)}`
									: `Excedido: ${formatMoney(Math.abs(remaining))}`}
							</Typography>
						</Box>
					</Box>
				</Box>
			</Box>

			{/* Content */}
			<Box
				ref={setScrollContainerRef}
				data-template104-scroll
				sx={{
					flex: 1,
					minHeight: 0,
					overflow: "auto",
					WebkitOverflowScrolling: "touch",
					padding: isSmallScreen ? "12px" : "16px",
				}}
			>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
					{budgetData.categories.map((category, categoryIndex) => {
						const categoryTotal = getCategoryTotal(category);
						return (
							<Box
								key={category.id}
								data-block-id={`text-${categoryIndex + 1}`}
								{...templateItemProps}
								sx={{
									transform:
										categoryIndex % 2 === 0
											? "rotate(-0.8deg)"
											: "rotate(1deg)",
									transition: "transform 0.2s ease",
								}}
							>
								<Box
									sx={{
										height: 8,
										backgroundColor: `${category.color}`,
										opacity: 0.85,
										borderRadius: "6px 6px 0 0",
										marginLeft: 2,
										marginRight: 2,
										border: "1px dashed rgba(255,255,255,0.5)",
									}}
								/>
								<Box
									sx={{
										backgroundColor: "#fff",
										borderRadius: "0 0 20px 20px",
										overflow: "hidden",
										boxShadow:
											"0 6px 24px rgba(232, 180, 188, 0.15), 0 2px 6px rgba(0,0,0,0.05)",
										border: "2px solid rgba(232, 180, 188, 0.25)",
										borderTop: "none",
									}}
								>
									{/* Category Header */}
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											padding: "12px 16px",
											borderBottom:
												category.expenses.length > 0
													? "1px solid #f1f5f9"
													: "none",
										}}
									>
										<Box
											sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
										>
											<Box
												sx={{
													width: 36,
													height: 36,
													borderRadius: "10px",
													backgroundColor: `${category.color}15`,
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													fontSize: "18px",
												}}
											>
												{category.emoji}
											</Box>
											<Box>
												<Typography
													sx={{
														fontSize: "14px !important",
														fontWeight: "600 !important",
														color: "#1E293B",
													}}
												>
													{category.name}
												</Typography>
												<Typography
													sx={{
														fontSize: "12px !important",
														color: "#64748B",
													}}
												>
													{category.expenses.length} gastos
												</Typography>
											</Box>
										</Box>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<Typography
												sx={{
													fontSize: "15px !important",
													fontWeight: "700 !important",
													color: category.color,
												}}
											>
												{formatMoney(categoryTotal)}
											</Typography>
											<IconButton
												{...templateItemProps}
												className={[templateItemProps?.className, "hide-in-viewer"].filter(Boolean).join(" ")}
												onClick={() => !isViewer && handleAddExpense(categoryIndex)}
												size="small"
												sx={{
													backgroundColor: `${category.color}10`,
													color: category.color,
													"&:hover": { backgroundColor: `${category.color}20` },
												}}
											>
												<AddIcon sx={{ fontSize: 18, pointerEvents: "none" }} />
											</IconButton>
										</Box>
									</Box>

									{/* Expenses */}
									{category.expenses.length > 0 && (
										<Box sx={{ padding: "8px 16px" }}>
											{category.expenses.map((expense, expenseIndex) => (
												<Box
													key={expense.id}
													sx={{
														display: "flex",
														alignItems: "center",
														justifyContent: "space-between",
														padding: "8px 0",
														borderBottom:
															expenseIndex < category.expenses.length - 1
																? "1px solid #f8fafc"
																: "none",
													}}
												>
													<Typography
														{...templateItemProps}
														onClick={() =>
															!isViewer && handleOpenEditExpense(categoryIndex, expenseIndex)
														}
														sx={{
															fontSize: "13px !important",
															color: "#475569",
															cursor: isViewer ? "default" : "pointer",
															"&:hover": { color: "#1E293B" },
														}}
													>
														{expense.description}
													</Typography>
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															gap: 1,
														}}
													>
														<Typography
															{...templateItemProps}
															onClick={() =>
																!isViewer && handleOpenEditExpense(categoryIndex, expenseIndex)
															}
															sx={{
																fontSize: "13px !important",
																fontWeight: "600 !important",
																color: "#1E293B",
																cursor: isViewer ? "default" : "pointer",
															}}
														>
															{formatMoney(expense.amount)}
														</Typography>
														<IconButton
															{...templateItemProps}
															className={[templateItemProps?.className, "hide-in-viewer"].filter(Boolean).join(" ")}
															onClick={() =>
																!isViewer && handleDeleteExpense(categoryIndex, expenseIndex)
															}
															size="small"
															sx={{ opacity: 0.4, "&:hover": { opacity: 1 } }}
														>
															<DeleteIcon sx={{ fontSize: 14, pointerEvents: "none" }} />
														</IconButton>
													</Box>
												</Box>
											))}
										</Box>
									)}
								</Box>
							</Box>
						);
					})}
				</Box>
			</Box>

			{/* Add / Edit Expense Modal - diseño coherente con Template103 (oculto en viewer) */}
			<Box className="hide-in-viewer">
			<BasicModal
				width={400}
				showModal={addExpenseModal || editExpenseModal}
				handleClose={handleCloseExpenseModal}
				contentSx={modalContentSx}
			>
				<Box sx={{ p: 3 }}>
					<Typography
						sx={{
							fontSize: "18px !important",
							fontWeight: 700,
							color: TRAVEL_KIT_MODAL.textPrimary,
							mb: 2,
							fontFamily: "'Inter', sans-serif",
						}}
					>
						{editExpenseModal ? "Editar gasto" : "Añadir gasto"}
					</Typography>
					<TextField
						label="Descripción"
						value={tempExpense.description}
						onChange={(e) =>
							setTempExpense({ ...tempExpense, description: e.target.value })
						}
						fullWidth
						size="small"
						margin="normal"
						placeholder="Ej: Cena en restaurante"
						sx={textFieldSx}
					/>
					<TextField
						label={`Cantidad (${budgetData.currency})`}
						value={tempExpense.amount}
						onChange={(e) =>
							setTempExpense({ ...tempExpense, amount: e.target.value })
						}
						fullWidth
						size="small"
						margin="normal"
						type="number"
						placeholder="0.00"
						sx={textFieldSx}
					/>
					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							gap: 1.5,
							mt: 3,
						}}
					>
						<Button
							onClick={handleCloseExpenseModal}
							sx={secondaryButtonSx}
						>
							Cancelar
						</Button>
						<Button
							variant="contained"
							onClick={handleSaveExpense}
							sx={primaryButtonSx}
						>
							{editExpenseModal ? "Guardar" : "Añadir"}
						</Button>
					</Box>
				</Box>
			</BasicModal>
			</Box>

			{/* Edit Budget Modal - diseño coherente con Template103 */}
			<Box className="hide-in-viewer">
			<BasicModal
				width={400}
				showModal={editBudgetModal}
				handleClose={() => setEditBudgetModal(false)}
				contentSx={modalContentSx}
			>
				<Box sx={{ p: 3 }}>
					<Typography
						sx={{
							fontSize: "18px !important",
							fontWeight: 700,
							color: TRAVEL_KIT_MODAL.textPrimary,
							mb: 2,
							fontFamily: "'Inter', sans-serif",
						}}
					>
						Editar presupuesto
					</Typography>
					<TextField
						label={`Presupuesto total (${budgetData.currency})`}
						value={tempBudget}
						onChange={(e) => setTempBudget(e.target.value)}
						fullWidth
						size="small"
						margin="normal"
						type="number"
						placeholder="0.00"
						sx={textFieldSx}
					/>
					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							gap: 1.5,
							mt: 3,
						}}
					>
						<Button
							onClick={() => setEditBudgetModal(false)}
							sx={secondaryButtonSx}
						>
							Cancelar
						</Button>
						<Button
							variant="contained"
							onClick={handleSaveBudget}
							sx={primaryButtonSx}
						>
							Guardar
						</Button>
					</Box>
				</Box>
			</BasicModal>
			</Box>
		</Box>
	);
};

Template104.thumbnail = "/images/templates/template-tk-budget.png";

export default Template104;
